import React, { useEffect, useRef, useState } from "react";
import { FaPhone, FaEnvelope } from 'react-icons/fa'; // Import icons from react-icons
import { FaCheckCircle } from "react-icons/fa";

function VisaWorld() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 10); // Adjust this based on the number of sections

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // Trigger when 10% of section is visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    // Calculate scroll position relative to the content box
    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const contentElement = contentRef.current;
      const titleElement = titleRef.current;

      if (contentElement && titleElement) {
        const { scrollTop, scrollHeight, clientHeight } = contentElement;
        const scrollPercentage = scrollTop / (scrollHeight - clientHeight);

        titleElement.scrollTo({
          top:
            scrollPercentage *
            (titleElement.scrollHeight - titleElement.clientHeight),
          behavior: "smooth",
        });
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return (
    <>
      <div className="flex flex-wrap">
        <div className="mt-10 flex flex-col items-center justify-center px-4 py-7 md:justify-between md:px-20 xl:flex-row"></div>

        <div className="mb-20 mt-5 flex min-h-screen ">
          <div
            ref={titleRef}
            className="sticky top-20 hidden h-screen w-1/4 overflow-y-auto bg-gray-100 p-4 md:block"
          >
            <ul className="space-y-2">
              <li
                onClick={() => scrollToSection(0)}
                className={`cursor-pointer p-12 ${activeIndex === 0 ? "bg-blue-500 text-white" : "bg-white text-[#2C3F69]" } shadow rounded-md`}
              >
                Permanent Residency Visa
              </li>
              <li
                onClick={() => scrollToSection(1)}
                className={`cursor-pointer p-8 ${activeIndex === 1 ? "bg-blue-500 text-white" : "bg-white text-[#2C3F69]" } shadow rounded-md`}
              >
                Express Entry for Canada
              </li>
              <li
                onClick={() => scrollToSection(2)}
                className={`cursor-pointer p-12 ${activeIndex === 2 ? "bg-blue-500 text-white" : "bg-white text-[#2C3F69]" } shadow rounded-md`}
              >
                Immigration Criteria
              </li>
            </ul>
          </div>

          <div
            ref={contentRef}
            className="mb-10 h-screen overflow-y-auto p-4 md:w-3/4 lg:w-3/4 xl:w-3/4"
          >
            <div ref={(el) => (sectionRefs.current[0] = el)}>
              <p className="text-3xl font-bold text-[#011473]">
                Permanent Residency Visa
              </p>
              <p className="mt-4 text-lg text-[#2C3F69]">
                At VisaWorld, we are committed to helping you achieve your aspiration of becoming a Canadian permanent resident. The Canadian government plans to issue over 341,000 permanent residence visas, including opportunities under the Federal Skilled Worker Program and Provincial Nominee Programs (PNPs).
              </p>
              <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
                <li>Access to world-class healthcare services</li>
                <li>Freedom to live, work and study across Canada</li>
                <li>Eligibility to apply for Canadian citizenship</li>
                <li>Protection by Canadian law and the Charter of Rights and Freedoms</li>
              </ul>
            </div>

            <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
              <p className="text-3xl font-bold text-[#011473]">
                Canada Express Entry (Permanent Resident / Economic Class / Skilled Migrants)
              </p>
              <p className="mt-4 text-lg text-[#2C3F69]">
                VisaWorld specializes in guiding you through the Express Entry system, established in 2015 to facilitate skilled migration applications. Through Express Entry, you can apply under various programs:
              </p>
              <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
                <li>Federal Skilled Worker Program</li>
                <li>Federal Skilled Trades Program</li>
                <li>Canadian Experience Class</li>
                <li>Provincial Nominee Programs (PNPs)</li>
              </ul>
            </div>

            <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
              <p className="text-3xl font-bold text-[#011473]">Eligibility Requirements</p>
              <p className="mt-4 text-lg text-[#2C3F69]">
                To successfully apply for Canadian permanent residency with VisaWorld, please ensure you meet the following criteria:
              </p>
              <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
                <li>Select an occupation that aligns with the skilled occupation list or obtain a provincial nomination</li>
                <li>Complete a skills assessment for your nominated occupation</li>
                <li>Meet the age requirements specific to the program you wish to apply for</li>
                <li>Secure the required eligibility score</li>
                <li>Provide evidence of proficiency in English or French</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

    

      <section className="bg-white py-8 px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#011473] mb-6">
            How Visa World Supports You
          </h2>

          <ul className="space-y-4">
            <li className="flex items-start">
              <FaCheckCircle className="text-blue-500 w-6 h-6 flex-shrink-0 mt-1" />
              <p className="ml-4 text-gray-700">
                Our team of experts works with you directly to understand your immigration goals and craft personalized solutions that comply with relevant legal frameworks.
              </p>
            </li>

            <li className="flex items-start">
              <FaCheckCircle className="text-blue-500 w-6 h-6 flex-shrink-0 mt-1" />
              <p className="ml-4 text-gray-700">
                We simplify the complex visa application process, handling paperwork and administrative tasks to ensure a smooth experience from start to finish.
              </p>
            </li>

            <li className="flex items-start">
              <FaCheckCircle className="text-blue-500 w-6 h-6 flex-shrink-0 mt-1" />
              <p className="ml-4 text-gray-700">
                With Visa World, you are supported by a network of professionals with years of experience in the immigration sector, ensuring your success at every step.
              </p>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}

export default VisaWorld;
